import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment
} from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { openInNewTab } from "../../../utils/downloadFile"
import Table from "../../table"
import EngagementsCell from "./cells/engagements"
import MenuCell from "./cells/menu"
import OwnerCell from "./cells/owner"
import ViewDashboardCell from "./cells/view-dashboard"
import ManageEngagementPanel from "../manage"
import SubscriptionStats, { ChartClickData } from "../subscription-stats"
import FilterChips from "../../charts/filter-chips"
import {
  useFetchSubscriptionDetailsQuery,
  useFetchSubscriptionLogsV2Query
} from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  checkIsAdmin,
  checkIsSuperAdmin,
  copyToClipboard,
  dateFilterToTimestamps
} from "../../../utils"
import {
  SubscriptionLogV2,
  BBColumnDef,
  EngagementsTab
} from "../../../../types"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import NoResultsInRangeMessage from "../../activity/no-results-in-range-message"
import {
  ColumnFiltersState,
  FilterFnOption,
  PaginationState
} from "@tanstack/react-table"

const PAGE_SIZE = 25

interface EngagementsTableProps {
  brandId?: string
  brandName?: string
  showStats?: boolean
  showDateFilter?: boolean
}

const EngagementsTable = ({
  brandId,
  brandName,
  showStats = true,
  showDateFilter = true
}: EngagementsTableProps = {}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const userDetails = useAppSelector(selectUserDetails)

  const isAdmin = useMemo(
    () => checkIsAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )
  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )

  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const dateFilterRef = useRef<DateFilterHandle>(null)
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<string>("")
  const [chartFilter, setChartFilter] = useState<ChartClickData | null>(null)

  // Server-side pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const {
    data: selectedSubscription,
    isFetching: isFetchingSelectedSubscription
  } = useFetchSubscriptionDetailsQuery(
    { id: selectedSubscriptionId },
    { skip: !selectedSubscriptionId }
  )

  // Keep panel closed during refetch to prevent it from reopening
  // Only close if we're refetching AND we already have data (not initial load)
  useEffect(() => {
    if (
      isFetchingSelectedSubscription &&
      selectedSubscriptionId &&
      selectedSubscription
    ) {
      // During refetch after update, ensure panel stays closed
      setSelectedSubscriptionId("")
    }
  }, [
    isFetchingSelectedSubscription,
    selectedSubscriptionId,
    selectedSubscription
  ])

  const openLink = useCallback((link: string) => {
    if (link) {
      openInNewTab(link)
    }
  }, [])

  const navigateToDetails = useCallback(
    (id: string) => {
      // Navigate with returnUrl to preserve brand context (like Leads tab)
      if (brandId) {
        const returnUrl = encodeURIComponent(
          location.pathname + location.search
        )
        navigate(
          `/engagements/${id}?returnUrl=${returnUrl}&brandName=${encodeURIComponent(
            brandName || ""
          )}`
        )
      } else {
        navigate(`/engagements/${id}`)
      }
    },
    [navigate, brandId, brandName, location]
  )

  const resetSelectedSubscriptionId = useCallback(
    () => setSelectedSubscriptionId(""),
    []
  )

  const columns: BBColumnDef<SubscriptionLogV2>[] = useMemo(
    () => [
      {
        id: "engagements",
        header: "Subscriptions",
        accessorFn: row => `${row.first_name} ${row.last_name} ${row.email}`,
        footer: props => props.column.id,
        cell: props => (
          <EngagementsCell
            info={props.row.original}
            onClick={navigateToDetails}
          />
        ),
        colSpan: 2,
        filterFn:
          "rowStatusFilter" as unknown as FilterFnOption<SubscriptionLogV2>
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.owner_email,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell info={props.row.original} onClick={navigateToDetails} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "view-dashboard",
        header: "",
        accessorFn: row => row.id,
        footer: props => props.column.id,
        cell: props => <ViewDashboardCell info={props.row.original} />,
        meta: { headerClassName: "hidden md:table-cell" },
        enableSorting: false
      },
      {
        id: "created",
        header: "",
        accessorFn: row => row.created,
        footer: props => props.column.id,
        cell: props => "",
        colSpan: 0,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => (
          <MenuCell
            info={props.row.original}
            onCopyDashboardLink={copyToClipboard}
            onEdit={setSelectedSubscriptionId}
            onViewLeadDashboard={openLink}
          />
        )
      }
    ],
    [navigateToDetails, openLink]
  )

  const tabKey = EngagementsTab.engagements
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
      // Clear chart filter when manually changing date range
      setChartFilter(null)
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  const handleViewLast12Months = useCallback(() => {
    const now = new Date()
    const lastYear = new Date(now)
    lastYear.setDate(now.getDate() - 365)
    lastYear.setHours(0, 0, 0, 0)
    dispatch(
      updateRange({
        tabKey,
        range: "custom",
        customStartDate: lastYear,
        customEndDate: now
      })
    )
  }, [dispatch, tabKey])

  // Handle chart click to update date filter
  const handleChartClick = useCallback(
    (data: ChartClickData) => {
      setChartFilter(data)

      // Convert chart period to date range
      if (data.viewType === "daily") {
        // Parse ISO date string (e.g., "2025-10-29")
        const date = new Date(data.period)
        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: date,
            customEndDate: date
          })
        )
      } else {
        // Monthly view: "2025-January"
        const [year, monthName] = data.period.split("-")
        const monthMap: Record<string, number> = {
          January: 0,
          February: 1,
          March: 2,
          April: 3,
          May: 4,
          June: 5,
          July: 6,
          August: 7,
          September: 8,
          October: 9,
          November: 10,
          December: 11
        }
        const month = monthMap[monthName]

        const startOfMonth = new Date(parseInt(year), month, 1)
        const endOfMonth = new Date(parseInt(year), month + 1, 0)

        dispatch(
          updateRange({
            tabKey,
            range: "custom",
            customStartDate: startOfMonth,
            customEndDate: endOfMonth
          })
        )
      }
    },
    [dispatch, tabKey]
  )

  const handleClearChartFilter = useCallback(() => {
    setChartFilter(null)
  }, [])

  // Calculate timestamps from date range
  const { min: timestamp_min, max: timestamp_max } = useMemo(
    () => dateFilterToTimestamps(dateFilter),
    [dateFilter]
  )

  // Derive server-side active/deleted params from columnFilters
  const statusParams = useMemo(() => {
    const statusFilter = columnFilters.find(f => f.id === "engagements")
    if (!statusFilter?.value) return {}

    const val = statusFilter.value as {
      active?: "1"
      disabled?: "1"
      deleted?: "1"
    }
    const selected = Object.keys(val).filter(
      k => val[k as keyof typeof val] === "1"
    )

    // No filters selected = show all
    if (selected.length === 0) return {}

    // Single filter maps cleanly to server params
    if (selected.length === 1) {
      if (selected[0] === "active") return { active: 1 }
      if (selected[0] === "disabled") return { active: 0 }
      if (selected[0] === "deleted") return { deleted: 1 }
    }

    // Two filters: check if we can express it
    if (selected.length === 2) {
      // active + disabled = all non-deleted = deleted=0
      if (selected.includes("active") && selected.includes("disabled"))
        return { deleted: 0 }
      // active + deleted or disabled + deleted can't be cleanly expressed
      // Omit server params and let client-side rowStatusFilter handle it on the page
    }

    // All three selected = show all
    if (selected.length === 3) return {}

    return {}
  }, [columnFilters])

  // Reset page when search/filter/date params change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchTerm, columnFilters, timestamp_min, timestamp_max])

  // Handle search from Table component
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  // Build query params
  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...statusParams,
      ...(timestamp_min !== undefined &&
      timestamp_max !== undefined &&
      showDateFilter
        ? { min_created: timestamp_min, max_created: timestamp_max }
        : {}),
      // If brandId is provided, filter by org_id (brand's org)
      // Otherwise use default role-based filtering
      ...(brandId
        ? { org_id: orgId }
        : isSuperAdmin
        ? {}
        : isAdmin
        ? { org_id: orgId }
        : { loid })
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      searchTerm,
      statusParams,
      timestamp_min,
      timestamp_max,
      showDateFilter,
      brandId,
      isSuperAdmin,
      isAdmin,
      orgId,
      loid
    ]
  )

  // Subscription logs (V2 paginated endpoint)
  const { data: paginatedResponse, isFetching } =
    useFetchSubscriptionLogsV2Query(queryParams)

  const subscriptionLogs = useMemo(() => {
    const allLogs = paginatedResponse?.data ?? []
    // If brandName is provided, filter by brand name
    if (brandName && allLogs.length > 0) {
      return allLogs.filter(log => log.brand_name === brandName)
    }
    return allLogs
  }, [paginatedResponse?.data, brandName])
  const totalRows = paginatedResponse?.pagination?.total ?? 0
  const totalPages = paginatedResponse?.pagination?.total_pages ?? 0

  // All-time existence check (page_size 1) - only when current range is empty and not "all"
  const isRangeEmpty = !isFetching && subscriptionLogs.length === 0
  const isNotAllTimeRange = dateFilter.range !== "all"
  const { data: anyHistoricalResponse, isFetching: isFetchingAllTime } =
    useFetchSubscriptionLogsV2Query(
      {
        page: 1,
        page_size: 1,
        ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
      },
      { skip: !isRangeEmpty || !isNotAllTimeRange }
    )
  const hasAnyHistorical = (anyHistoricalResponse?.pagination?.total ?? 0) > 0
  const isCheckingHistorical =
    isRangeEmpty && isNotAllTimeRange && isFetchingAllTime
  const shouldHideHeader = !isFetching && subscriptionLogs.length === 0

  return (
    <div>
      <Table<SubscriptionLogV2>
        className="engagements-table"
        isLoading={isFetching}
        data={subscriptionLogs}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="engagements"
        searchPlaceholder="Search subscriptions…"
        pagination={pagination}
        setPagination={setPagination}
        pageCount={totalPages}
        rowCount={totalRows}
        onSearchChange={handleSearchChange}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        pageSize={PAGE_SIZE}
        extraHeaderElement={
          showDateFilter ? (
            <DateFilter
              ref={dateFilterRef}
              selectedRange={dateFilter.range}
              customStartDate={dateFilter.customStartDate}
              customEndDate={dateFilter.customEndDate}
              onRangeChange={handleDateRangeChange}
              onClearFilter={handleClearDateFilter}
            />
          ) : undefined
        }
        extraHeaderElementClassName="ml-auto"
        shrinkFilterSort
        defaultSortBy="created"
        defaultSortDir="desc"
        extraElementBeforeTable={
          showStats ? (
            <Fragment>
              <SubscriptionStats
                orgId={isSuperAdmin ? undefined : orgId}
                loid={isSuperAdmin || isAdmin ? undefined : loid}
                onChartClick={handleChartClick}
                selectedBarIndex={chartFilter?.index ?? null}
              />
              {chartFilter && (
                <FilterChips
                  filter={chartFilter}
                  onClear={handleClearChartFilter}
                  currentCount={subscriptionLogs.length}
                  totalCount={totalRows}
                  itemLabel="subscriptions"
                />
              )}
            </Fragment>
          ) : undefined
        }
        hasColumnFilter={false}
        hasActiveDeletedFilter
        noHeader={shouldHideHeader}
        noDataElement={
          isCheckingHistorical ? (
            <td colSpan={columns.length} className="p-0">
              <div className="min-h-[400px]" />
            </td>
          ) : isRangeEmpty && hasAnyHistorical && !isFetchingAllTime ? (
            <td colSpan={columns.length} className="p-0">
              <NoResultsInRangeMessage
                type="engagements"
                onViewLast12Months={handleViewLast12Months}
                onSelectDateRange={() => dateFilterRef.current?.openAndFocus()}
              />
            </td>
          ) : undefined
        }
      />

      <ManageEngagementPanel
        data={selectedSubscription}
        open={!!(selectedSubscriptionId && selectedSubscription)}
        onClose={resetSelectedSubscriptionId}
      />
    </div>
  )
}

export default EngagementsTable
